import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "./ui/button";
import {
  Loader2,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Video,
  Image as ImageIcon,
  Target,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

// ============================================================
// Types
// ============================================================

interface CampaignRow {
  campaignId: string;
  campaignName: string;
  spend: string;
  leads: number;
  costPerLead: number;
  clicks: string;
  ctr: string;
  calls: string; // umówione rozmowy (schedules)
  costPerSchedule: string; // koszt za umówioną rozmowę (VP-61)
  sales: string; // sprzedaże
  revenue: string; // wartość zakupów
  roas: string;
}

interface AdSetRow {
  adSetId: string;
  adSetName: string;
  spend: string;
  leads: number;
  costPerLead: number;
  clicks: string;
  ctr: string;
  status: string;
  startTime: string | null;
  calls: string;
  costPerSchedule: string; // koszt za umówioną rozmowę (VP-61)
  sales: string;
  revenue: string;
  roas: string;
}

export interface AdRow {
  adId: string;
  adName: string;
  status: string;
  primaryText: string;
  headline: string;
  description: string;
  callToAction: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  videoId: string | null;
  linkUrl?: string;
}

export interface SelectedAdSetData {
  adSetId: string;
  adSetName: string;
  campaignId: string;
  targeting: Record<string, unknown>;
  optimizationGoal: string;
  destinationType: string;
  promotedObject?: Record<string, unknown> | null;
  dailyBudget?: number;
  lifetimeBudget?: number;
  instagramActorId?: string;
  sourcePageId?: string;
  brandedContentSponsorPageId?: string;
  ads: AdRow[];
  // Performance data
  spend: string;
  leads: number;
  costPerLead: number;
  clicks: string;
  ctr: string;
}

interface AdSetBrowserProps {
  adAccountId: string;
  datePreset: string;
  onSelect: (data: SelectedAdSetData) => void;
  onClose: () => void;
}

// Shared grid layout: Name | Start | Spend | Leads | Calls | Sales | ROAS | CPL | CPS | CTR
// Campaign rows skip Start (always —) but keep same grid for alignment
const GRID_CAMPAIGN = "grid grid-cols-[2fr_52px_72px_52px_52px_52px_52px_72px_72px_46px] gap-2";
const GRID_ADSET = "grid grid-cols-[2fr_52px_72px_52px_52px_52px_52px_72px_72px_46px] gap-2";

// ============================================================
// Sorting
// ============================================================

type SortDirection = "asc" | "desc";

type CampaignSortField =
  | "campaignName"
  | "spend"
  | "leads"
  | "calls"
  | "costPerSchedule"
  | "sales"
  | "roas"
  | "costPerLead"
  | "ctr";
type AdSetSortField =
  | "adSetName"
  | "spend"
  | "leads"
  | "calls"
  | "costPerSchedule"
  | "sales"
  | "roas"
  | "costPerLead"
  | "ctr"
  | "startTime";

interface SortState<F> {
  field: F;
  direction: SortDirection;
}

function numericVal(val: string | number): number {
  const n = typeof val === "string" ? parseFloat(val) : val;
  return isNaN(n) ? 0 : n;
}

const NUMERIC_FIELDS = new Set([
  "spend",
  "leads",
  "costPerLead",
  "clicks",
  "ctr",
  "calls",
  "sales",
  "roas",
]);
const DATE_FIELDS = new Set(["startTime"]);

function sortRows<T>(rows: T[], field: keyof T, direction: SortDirection): T[] {
  return [...rows].sort((a, b) => {
    const aVal = a[field];
    const bVal = b[field];
    let cmp: number;
    if (DATE_FIELDS.has(field as string)) {
      const aDate = aVal ? new Date(aVal as string).getTime() : 0;
      const bDate = bVal ? new Date(bVal as string).getTime() : 0;
      cmp = aDate - bDate;
    } else if (NUMERIC_FIELDS.has(field as string)) {
      cmp = numericVal(aVal as string | number) - numericVal(bVal as string | number);
    } else {
      cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
    }
    return direction === "asc" ? cmp : -cmp;
  });
}

// ============================================================
// Helpers
// ============================================================

function formatPLN(val: string | number, locale: string, suffix: string): string {
  const n = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(n) || n === 0) return "—";
  return `${n.toFixed(2)}${suffix}`;
}

function SortIcon({ field, sort }: { field: string; sort: SortState<string> }) {
  if (sort.field !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30 inline-block" />;
  return sort.direction === "asc" ? (
    <ArrowUp className="w-3 h-3 ml-1 text-purple-400 inline-block" />
  ) : (
    <ArrowDown className="w-3 h-3 ml-1 text-purple-400 inline-block" />
  );
}

// ============================================================
// Component
// ============================================================

export default function AdSetBrowser({
  adAccountId,
  datePreset,
  onSelect,
  onClose,
}: AdSetBrowserProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "pl" ? "pl-PL" : "en-US";
  const currencySuffix = i18n.language === "pl" ? " zł" : " $";
  const fmtCur = (v: string | number) => formatPLN(v, locale, currencySuffix);
  const fmtInt = (v: string | number) => {
    const n = Math.round(Number(v) || 0);
    return n > 0 ? n.toLocaleString(locale) : "—";
  };
  const fmtRoas = (v: string | number) => {
    const n = Number(v) || 0;
    return n > 0 ? `${n.toFixed(1)}x` : "—";
  };
  const { toast } = useToast();

  // Campaign level
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);

  // Ad set level
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [adSets, setAdSets] = useState<Record<string, AdSetRow[]>>({});
  const [loadingAdSets, setLoadingAdSets] = useState<string | null>(null);

  // Ad level
  const [expandedAdSet, setExpandedAdSet] = useState<string | null>(null);
  const [ads, setAds] = useState<
    Record<
      string,
      {
        ads: AdRow[];
        targeting: Record<string, unknown>;
        adSetName: string;
        optimizationGoal: string;
        destinationType: string;
        promotedObject?: Record<string, unknown> | null;
        dailyBudget?: number;
        lifetimeBudget?: number;
        instagramActorId?: string;
        sourcePageId?: string;
        brandedContentSponsorPageId?: string;
      }
    >
  >({});
  const [loadingAds, setLoadingAds] = useState<string | null>(null);

  // Selected ad set
  const [selectedAdSetId, setSelectedAdSetId] = useState<string | null>(null);

  // Sorting
  const [campaignSort, setCampaignSort] = useState<SortState<CampaignSortField>>({
    field: "spend",
    direction: "desc",
  });
  const [adSetSort, setAdSetSort] = useState<SortState<AdSetSortField>>({
    field: "leads",
    direction: "desc",
  });

  // Filtering
  const [statusFilter, setStatusFilter] = useState<string>("all");

  function toggleCampaignSort(field: CampaignSortField) {
    setCampaignSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "desc" }
    );
  }

  function toggleAdSetSort(field: AdSetSortField) {
    setAdSetSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "desc" }
    );
  }

  const sortedCampaigns = sortRows(campaigns, campaignSort.field, campaignSort.direction);

  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const data = await apiRequest(
        "GET",
        `/api/campaign-creator/browse-campaigns?adAccountId=${encodeURIComponent(adAccountId)}&datePreset=${datePreset}`
      );
      setCampaigns(data.campaigns || []);
      setCampaignsLoaded(true);
    } catch (err) {
      toast({
        title: t("adsetBrowser.toast.tryAgainTitle"),
        description: t("adsetBrowser.toast.tryAgainDesc"),
        variant: "destructive",
      });
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const fetchAdSets = async (campaignId: string) => {
    if (adSets[campaignId]) {
      setExpandedCampaign(expandedCampaign === campaignId ? null : campaignId);
      return;
    }
    setExpandedCampaign(campaignId);
    setLoadingAdSets(campaignId);
    try {
      const data = await apiRequest(
        "GET",
        `/api/campaign-creator/browse-adsets?adAccountId=${encodeURIComponent(adAccountId)}&campaignId=${encodeURIComponent(campaignId)}&datePreset=${datePreset}`
      );
      setAdSets((prev) => ({ ...prev, [campaignId]: data.adSets || [] }));
    } catch (err) {
      toast({
        title: t("adsetBrowser.toast.tryAgainTitle"),
        description: t("adsetBrowser.toast.tryAgainDesc"),
        variant: "destructive",
      });
    } finally {
      setLoadingAdSets(null);
    }
  };

  const fetchAdsForAdSet = async (adSetId: string) => {
    if (ads[adSetId]) {
      setExpandedAdSet(expandedAdSet === adSetId ? null : adSetId);
      return;
    }
    setExpandedAdSet(adSetId);
    setLoadingAds(adSetId);
    try {
      const data = await apiRequest(
        "GET",
        `/api/campaign-creator/browse-ads?adSetId=${encodeURIComponent(adSetId)}`
      );
      setAds((prev) => ({
        ...prev,
        [adSetId]: {
          ads: data.ads || [],
          targeting: data.targeting || {},
          adSetName: data.adSetName || "",
          optimizationGoal: data.optimizationGoal || "",
          destinationType: data.destinationType || "",
          promotedObject: data.promotedObject || null,
          dailyBudget: data.dailyBudget,
          lifetimeBudget: data.lifetimeBudget,
          instagramActorId: data.instagramActorId || "",
          sourcePageId: data.sourcePageId || "",
          brandedContentSponsorPageId: data.brandedContentSponsorPageId || "",
        },
      }));
    } catch (err) {
      toast({
        title: t("adsetBrowser.toast.tryAgainTitle"),
        description: t("adsetBrowser.toast.tryAgainDesc"),
        variant: "destructive",
      });
    } finally {
      setLoadingAds(null);
    }
  };

  const handleSelectAdSet = (adSetId: string, campaignId: string) => {
    const adSetData = ads[adSetId];
    const adSetRow = Object.values(adSets)
      .flat()
      .find((a) => a.adSetId === adSetId);
    if (!adSetData || !adSetRow) {
      toast({
        title: t("adsetBrowser.toast.tryAgainTitle"),
        description: t("adsetBrowser.toast.expandFirst"),
        variant: "destructive",
      });
      return;
    }

    setSelectedAdSetId(adSetId);
    onSelect({
      adSetId,
      adSetName: adSetData.adSetName || adSetRow.adSetName,
      campaignId,
      targeting: adSetData.targeting,
      optimizationGoal: adSetData.optimizationGoal,
      destinationType: adSetData.destinationType,
      promotedObject: adSetData.promotedObject || null,
      dailyBudget: adSetData.dailyBudget,
      lifetimeBudget: adSetData.lifetimeBudget,
      instagramActorId: adSetData.instagramActorId,
      sourcePageId: adSetData.sourcePageId,
      brandedContentSponsorPageId: adSetData.brandedContentSponsorPageId,
      ads: adSetData.ads,
      spend: adSetRow.spend,
      leads: adSetRow.leads,
      costPerLead: adSetRow.costPerLead,
      clicks: adSetRow.clicks,
      ctr: adSetRow.ctr,
    });
  };

  const colHdr = (
    label: string,
    field: CampaignSortField | AdSetSortField,
    isCampaign: boolean,
    align: "left" | "right" = "right"
  ) => (
    <button
      onClick={() =>
        isCampaign
          ? toggleCampaignSort(field as CampaignSortField)
          : toggleAdSetSort(field as AdSetSortField)
      }
      className={`flex items-center gap-0 text-[10px] uppercase tracking-wider font-semibold transition-colors hover:text-foreground ${
        (isCampaign ? campaignSort.field : adSetSort.field) === field
          ? "text-purple-500"
          : "text-foreground/60"
      } ${align === "right" ? "ml-auto" : ""}`}
    >
      {label}
      <SortIcon
        field={field}
        sort={isCampaign ? (campaignSort as SortState<string>) : (adSetSort as SortState<string>)}
      />
    </button>
  );

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-purple-500" />
          <span className="text-xs font-medium text-foreground">{t("adsetBrowser.title")}</span>
        </div>
        <div className="flex items-center gap-2">
          {campaignsLoaded && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-[10px] bg-muted border border-border text-foreground rounded px-1.5 py-0.5 cursor-pointer hover:bg-muted/80 transition-colors"
            >
              <option value="all">Wszystkie</option>
              <option value="ACTIVE">Aktywne</option>
              <option value="PAUSED">Zatrzymane</option>
            </select>
          )}
          {!campaignsLoaded && (
            <Button
              onClick={fetchCampaigns}
              disabled={loadingCampaigns}
              variant="outline"
              size="sm"
              className="border-purple-500/30 text-purple-600 dark:text-purple-300 hover:bg-purple-500/10 text-xs h-7"
            >
              {loadingCampaigns ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {t("adsetBrowser.fetchCampaigns")}
            </Button>
          )}
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-xs px-2 py-1"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Campaign tree */}
      {campaignsLoaded && campaigns.length === 0 && (
        <div className="p-4 text-center text-xs text-muted-foreground">
          {t("adsetBrowser.noCampaigns")}
        </div>
      )}

      {campaigns.length > 0 && (
        <div className="max-h-[400px] overflow-y-auto">
          {/* Campaign table header — sortable, no Start column (campaigns don't have start date) */}
          <div
            className={`${GRID_CAMPAIGN} px-4 py-2 border-b border-border bg-muted/50 sticky top-0 z-10`}
          >
            <div>{colHdr(t("adsetBrowser.col.name"), "campaignName", true, "left")}</div>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground text-right">
              —
            </span>
            {colHdr(t("adsetBrowser.col.spend"), "spend", true)}
            {colHdr(t("adsetBrowser.col.leads"), "leads", true)}
            {colHdr(t("adsetBrowser.col.calls"), "calls", true)}
            {colHdr(t("adsetBrowser.col.sales"), "sales", true)}
            {colHdr(t("adsetBrowser.col.roas"), "roas", true)}
            {colHdr(t("adsetBrowser.col.cpl"), "costPerLead", true)}
            {colHdr(t("adsetBrowser.col.cps"), "costPerSchedule", true)}
            {colHdr(t("adsetBrowser.col.ctr"), "ctr", true)}
          </div>

          {sortedCampaigns.map((campaign) => (
            <div key={campaign.campaignId}>
              {/* Campaign row */}
              <button
                onClick={() => fetchAdSets(campaign.campaignId)}
                className={`w-full ${GRID_CAMPAIGN} px-4 py-2.5 text-left hover:bg-muted/40 transition-colors border-b border-border/50`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {loadingAdSets === campaign.campaignId ? (
                    <Loader2 className="w-3 h-3 animate-spin text-purple-500 shrink-0" />
                  ) : expandedCampaign === campaign.campaignId ? (
                    <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                  )}
                  <span className="text-xs text-foreground truncate">{campaign.campaignName}</span>
                </div>
                <span className="text-[11px] text-muted-foreground/30 text-right">—</span>
                <span className="text-[11px] text-muted-foreground text-right">
                  {fmtCur(campaign.spend)}
                </span>
                <span className="text-[11px] text-right">
                  <span
                    className={
                      campaign.leads > 0
                        ? "text-green-600 dark:text-green-400"
                        : "text-muted-foreground/50"
                    }
                  >
                    {campaign.leads || "—"}
                  </span>
                </span>
                <span className="text-[11px] text-right">
                  <span
                    className={
                      Number(campaign.calls) > 0
                        ? "text-cyan-600 dark:text-cyan-400"
                        : "text-muted-foreground/50"
                    }
                  >
                    {fmtInt(campaign.calls)}
                  </span>
                </span>
                <span className="text-[11px] text-right">
                  <span
                    className={
                      Number(campaign.sales) > 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-muted-foreground/50"
                    }
                  >
                    {fmtInt(campaign.sales)}
                  </span>
                </span>
                <span className="text-[11px] text-right">
                  <span
                    className={
                      Number(campaign.roas) > 0
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground/50"
                    }
                  >
                    {fmtRoas(campaign.roas)}
                  </span>
                </span>
                <span className="text-[11px] text-right text-muted-foreground">
                  {campaign.costPerLead > 0 ? fmtCur(campaign.costPerLead) : "—"}
                </span>
                <span className="text-[11px] text-right text-muted-foreground">
                  {Number(campaign.costPerSchedule) > 0 ? fmtCur(campaign.costPerSchedule) : "—"}
                </span>
                <span className="text-[11px] text-muted-foreground text-right">
                  {campaign.ctr}%
                </span>
              </button>

              {/* Ad sets */}
              {expandedCampaign === campaign.campaignId && adSets[campaign.campaignId] && (
                <div className="bg-muted/20">
                  {/* Ad set sort header — includes Start column */}
                  <div
                    className={`${GRID_ADSET} px-4 py-1.5 border-b border-border/50 bg-muted/40 sticky top-[37px] z-10`}
                  >
                    <div className="pl-5">
                      {colHdr(t("adsetBrowser.col.name"), "adSetName", false, "left")}
                    </div>
                    {colHdr("Start", "startTime", false)}
                    {colHdr(t("adsetBrowser.col.spend"), "spend", false)}
                    {colHdr(t("adsetBrowser.col.leads"), "leads", false)}
                    {colHdr(t("adsetBrowser.col.calls"), "calls", false)}
                    {colHdr(t("adsetBrowser.col.sales"), "sales", false)}
                    {colHdr(t("adsetBrowser.col.roas"), "roas", false)}
                    {colHdr(t("adsetBrowser.col.cpl"), "costPerLead", false)}
                    {colHdr(t("adsetBrowser.col.cps"), "costPerSchedule", false)}
                    {colHdr(t("adsetBrowser.col.ctr"), "ctr", false)}
                  </div>

                  {sortRows(
                    adSets[campaign.campaignId].filter(
                      (a) => statusFilter === "all" || a.status === statusFilter
                    ),
                    adSetSort.field,
                    adSetSort.direction
                  ).map((adSet) => (
                    <div key={adSet.adSetId}>
                      {/* Ad set row */}
                      <div
                        className={`${GRID_ADSET} px-4 py-2 border-b border-border/30 hover:bg-muted/30 transition-colors`}
                      >
                        <div className="flex items-center gap-2 min-w-0 pl-5">
                          <button
                            onClick={() => fetchAdsForAdSet(adSet.adSetId)}
                            className="flex items-center gap-1.5 min-w-0"
                          >
                            {loadingAds === adSet.adSetId ? (
                              <Loader2 className="w-3 h-3 animate-spin text-cyan-500 shrink-0" />
                            ) : expandedAdSet === adSet.adSetId ? (
                              <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                            )}
                            <span className="text-[11px] text-foreground/80 truncate">
                              ↳ {adSet.adSetName}
                            </span>
                          </button>
                          <span
                            className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded border font-medium ${
                              adSet.status === "ACTIVE"
                                ? "border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400"
                                : adSet.status === "PAUSED"
                                  ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400"
                                  : "border-border bg-muted text-muted-foreground"
                            }`}
                          >
                            {adSet.status === "ACTIVE"
                              ? "Aktywny"
                              : adSet.status === "PAUSED"
                                ? "Zatrzymany"
                                : adSet.status}
                          </span>
                          {/* Select button */}
                          {ads[adSet.adSetId] && (
                            <button
                              onClick={() => handleSelectAdSet(adSet.adSetId, campaign.campaignId)}
                              className={`ml-auto shrink-0 text-[10px] px-2 py-0.5 rounded border transition-colors ${
                                selectedAdSetId === adSet.adSetId
                                  ? "border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-400"
                                  : "border-purple-500/40 bg-purple-500/5 text-purple-700 dark:text-purple-300 hover:bg-purple-500/10"
                              }`}
                            >
                              {selectedAdSetId === adSet.adSetId ? (
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> {t("adsetBrowser.selected")}
                                </span>
                              ) : (
                                t("adsetBrowser.select")
                              )}
                            </button>
                          )}
                        </div>
                        <span className="text-[10px] text-muted-foreground text-right">
                          {adSet.startTime
                            ? new Date(adSet.startTime).toLocaleDateString("pl-PL", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "2-digit",
                              })
                            : "—"}
                        </span>
                        <span className="text-[11px] text-muted-foreground text-right">
                          {fmtCur(adSet.spend)}
                        </span>
                        <span className="text-[11px] text-right">
                          <span
                            className={
                              adSet.leads > 0
                                ? "text-green-600 dark:text-green-400"
                                : "text-muted-foreground/50"
                            }
                          >
                            {adSet.leads || "—"}
                          </span>
                        </span>
                        <span className="text-[11px] text-right">
                          <span
                            className={
                              Number(adSet.calls) > 0
                                ? "text-cyan-600 dark:text-cyan-400"
                                : "text-muted-foreground/50"
                            }
                          >
                            {fmtInt(adSet.calls)}
                          </span>
                        </span>
                        <span className="text-[11px] text-right">
                          <span
                            className={
                              Number(adSet.sales) > 0
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-muted-foreground/50"
                            }
                          >
                            {fmtInt(adSet.sales)}
                          </span>
                        </span>
                        <span className="text-[11px] text-right">
                          <span
                            className={
                              Number(adSet.roas) > 0
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-muted-foreground/50"
                            }
                          >
                            {fmtRoas(adSet.roas)}
                          </span>
                        </span>
                        <span className="text-[11px] text-right text-muted-foreground">
                          {adSet.costPerLead > 0 ? fmtCur(adSet.costPerLead) : "—"}
                        </span>
                        <span className="text-[11px] text-right text-muted-foreground">
                          {Number(adSet.costPerSchedule) > 0 ? fmtCur(adSet.costPerSchedule) : "—"}
                        </span>
                        <span className="text-[11px] text-muted-foreground text-right">
                          {adSet.ctr}%
                        </span>
                      </div>

                      {/* Ads inside ad set */}
                      {expandedAdSet === adSet.adSetId && ads[adSet.adSetId] && (
                        <div className="bg-muted/10 border-l-2 border-purple-500/20 ml-8">
                          {ads[adSet.adSetId].ads.map((ad) => (
                            <div
                              key={ad.adId}
                              className="flex items-start gap-2.5 px-4 py-2 border-b border-border/30"
                            >
                              {/* Thumbnail */}
                              <div className="w-10 h-10 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                                {ad.thumbnailUrl || ad.imageUrl ? (
                                  <img
                                    src={ad.thumbnailUrl || ad.imageUrl!}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : ad.videoId ? (
                                  <Video className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[11px] text-foreground/70 truncate">
                                    {ad.adName}
                                  </span>
                                  {ad.videoId && (
                                    <Video className="w-3 h-3 text-blue-500/50 shrink-0" />
                                  )}
                                  {ad.status === "ACTIVE" && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                                  )}
                                </div>
                                {ad.primaryText && (
                                  <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                                    {ad.primaryText.slice(0, 100)}
                                    {ad.primaryText.length > 100 ? "..." : ""}
                                  </p>
                                )}
                                {ad.headline && (
                                  <p className="text-[10px] text-muted-foreground/70 truncate">
                                    H: {ad.headline}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                          {ads[adSet.adSetId].ads.length === 0 && (
                            <div className="px-4 py-2 text-[10px] text-muted-foreground">
                              {t("adsetBrowser.noAds")}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
